'use strict';

/**
 * PictView-BlueprintComposer
 * --------------------------
 * The editable Blueprint, standalone or entity-owned: compose a named drafting sheet by pinning
 * build-graph artifacts (Visions, Modules, Features, Whiteboards) onto it. A standalone Blueprint has
 * no auto-gathered set -- it is exactly what you add here. An owned Blueprint (a Vision/Module/Feature's
 * own sheet) already draws an auto-gathered set from its manifest (WI #340); those plates render too, as
 * the read view does, but read-only -- there is nothing to unpin because auto-gather is not a pin. Each
 * pinnable type is a plate: "+ Add" opens a picker of candidates (the customer's artifacts of that type
 * not already on the sheet); an item pinned this way (Pinned true, carrying an IDBlueprintItem) gets a
 * remove control, an auto-gathered one (Pinned false) gets an "AUTO" tag instead. The pins are
 * BlueprintItem rows keyed on IDBlueprint, so the read views (the Blueprint detail and the public
 * /shared/:token page) render the same sheet through BlueprintRender.
 *
 * Shown via #/blueprint/:id/compose (Pict-Application._renderBlueprintComposer). The shared drafting
 * styles come from BlueprintRender.SHEET_CSS so the composer and the rendered sheet match.
 */

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');
const libBlueprintRender = require('../BlueprintRender.js');

const _PINNABLE =
[
	{ Type: 'Vision', Label: 'Visions', One: 'vision' },
	{ Type: 'Module', Label: 'Modules', One: 'module' },
	{ Type: 'Feature', Label: 'Features', One: 'feature' },
	{ Type: 'Whiteboard', Label: 'Whiteboards', One: 'whiteboard' }
];

class PictViewBlueprintComposer extends libPictView
{
	onBeforeInitialize()
	{
		this._id = 0;
		this._data = null;
		this._loading = false;
		this._picker = null; // { Type, Candidates: [] }
		this._busy = false;
		return super.onBeforeInitialize();
	}

	_selfRef() { return "_Pict.views['" + this.Hash + "']"; }
	_icon(pType) { let tmpP = this.pict && this.pict.providers && this.pict.providers.Icon; return tmpP ? tmpP.get(libBlueprintRender.ICON_FOR[pType] || 'FileText') : ''; }
	_can(pEnt) { let tmpA = this.pict.PictApplication; return !!(tmpA && typeof tmpA._can === 'function' && tmpA._can(pEnt)); }
	_esc(pS) { return String(pS == null ? '' : pS).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;'); }
	_toast(pMsg, pType) { let tmpM = this.pict.views['Pict-Section-Modal']; if (tmpM && typeof tmpM.toast === 'function') { tmpM.toast(pMsg, { type: pType || 'info' }); } }

	show(pID)
	{
		this._id = parseInt(pID, 10) || 0;
		this._data = null; this._picker = null; this._loading = true;
		this.render();
		if (!this._id) { this._loading = false; return; }
		this._reload();
	}

	_reload()
	{
		return libFrameworkClient.getJSON(this.pict, '/1.0/Blueprint/' + this._id + '/Items')
			.then((pD) => { this._data = pD; this._loading = false; if (this.pict.PictApplication && typeof this.pict.PictApplication._setPageTitle === 'function') { this.pict.PictApplication._setPageTitle((pD && pD.Title) ? (pD.Title + ' (composing)') : 'Blueprint Composer'); } this.render(); })
			.catch(() => { this._data = null; this._loading = false; this.render(); });
	}

	back()
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (tmpRouter && this._id) { tmpRouter.navigate('/blueprint/' + this._id); }
	}

	openPicker(pType)
	{
		if (this._busy) { return; }
		this._busy = true;
		libFrameworkClient.getJSON(this.pict, '/1.0/Blueprint/' + this._id + '/Candidates/' + encodeURIComponent(pType))
			.then((pD) => { this._busy = false; this._picker = { Type: pType, Candidates: (pD && pD.Candidates) || [] }; this.render(); })
			.catch(() => { this._busy = false; this._toast('Could not load options.', 'error'); });
	}
	closePicker() { this._picker = null; this.render(); }

	pin(pType, pIDItem)
	{
		if (this._busy) { return; }
		this._busy = true;
		libFrameworkClient.postJSON(this.pict, '/1.0/Blueprint/' + this._id + '/Item', { ItemType: pType, IDItem: parseInt(pIDItem, 10) || 0 })
			.then(() => { this._busy = false; this._picker = null; return this._reload(); })
			.catch(() => { this._busy = false; this._toast('Could not add that.', 'error'); });
	}

	unpin(pIDBlueprintItem)
	{
		if (this._busy) { return; }
		this._busy = true;
		libFrameworkClient.delJSON(this.pict, '/1.0/Blueprint/' + this._id + '/Item/' + (parseInt(pIDBlueprintItem, 10) || 0))
			.then(() => { this._busy = false; return this._reload(); })
			.catch(() => { this._busy = false; this._toast('Could not remove that.', 'error'); });
	}

	// Make this blueprint the plan sheet's cover (WI #339), or remove it if it already is. Writes through
	// PUT /1.0/Plansheet/Cover; IDBlueprint 0 clears. The button reflects _data.IsCover.
	toggleCover()
	{
		if (this._busy || !this._id) { return; }
		this._busy = true;
		let tmpIsCover = !!(this._data && this._data.IsCover);
		let tmpTarget = tmpIsCover ? 0 : this._id;
		libFrameworkClient.putJSON(this.pict, '/1.0/Plansheet/Cover', { IDBlueprint: tmpTarget })
			.then((pBody) =>
			{
				this._busy = false;
				if (pBody && pBody.Success)
				{
					if (this._data) { this._data.IsCover = (tmpTarget === this._id); }
					this._toast(tmpTarget ? 'This blueprint is now the plan sheet cover.' : 'Removed as the plan sheet cover.', 'success');
					this.render();
					return;
				}
				this._toast((pBody && pBody.Error) || 'Could not set the cover.', 'error');
			})
			.catch((pErr) => { this._busy = false; this._toast((pErr && pErr.message) || 'Could not set the cover.', 'error'); });
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		if (typeof document !== 'undefined')
		{
			let tmpHost = document.getElementById('BlueprintComposer-Body');
			if (tmpHost) { tmpHost.innerHTML = this._bodyHTML(); }
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_bodyHTML()
	{
		if (this._loading) { return '<div class="bp-empty">Loading the blueprint...</div>'; }
		if (!this._data) { return '<div class="bp-empty">This blueprint could not be loaded.</div>'; }
		let tmpSelf = this._selfRef();
		let tmpCanEdit = this._can('content.edit');
		let tmpNum = this._data.BlueprintNumber ? ('BP-' + String(this._data.BlueprintNumber).padStart(3, '0')) : ('BP #' + this._id);
		let tmpDesc = this._data.Description ? ('<p class="bp-desc">' + this._esc(this._data.Description) + '</p>') : '';

		let tmpByType = {};
		(this._data.Groups || []).forEach((pG) => { tmpByType[pG.Type] = pG; });

		// Plates in a stable order: the four curatable types first, even empty, so "+ Add" is always
		// reachable there; then whatever else the payload gathered (an owned blueprint's manifest types --
		// WorkItem, Session, Media, ...), in the order the server sent them. Those extra plates are
		// read-only here: nothing in them was pinned, so there is nothing to remove.
		let tmpPinnableTypes = {};
		_PINNABLE.forEach((pPT) => { tmpPinnableTypes[pPT.Type] = true; });
		let tmpPlates = _PINNABLE.map((pPT) => ({ Type: pPT.Type, Label: pPT.Label, One: pPT.One, Curatable: true }));
		(this._data.Groups || []).forEach((pG) => { if (!tmpPinnableTypes[pG.Type]) { tmpPlates.push({ Type: pG.Type, Label: pG.Label || pG.Type, Curatable: false }); } });

		let tmpHTML = '<div class="bp-sheet">'
			+ '<div class="bp-titleblock"><div class="bp-tb-main"><div class="bp-eyebrow">BLUEPRINT | ' + this._esc(tmpNum) + '</div>'
			+ '<h1 class="bp-h1">' + this._esc(this._data.Title || 'Blueprint') + '</h1>' + tmpDesc + '</div>';
			// "Set as cover" (WI #339): use this blueprint as the plan sheet's cover across the app. The
			// active state (this is the cover) shows a solid accent button that removes on click.
			let tmpCoverBtn = tmpCanEdit
				? ('<button class="bp-act' + (this._data.IsCover ? '' : ' bp-act-ghost') + '" onclick="' + tmpSelf + '.toggleCover()" title="'
					+ (this._data.IsCover ? 'This blueprint is this plan sheet\'s cover. Click to remove.' : 'Use this blueprint to represent this plan sheet across the app.') + '">'
					+ (this._data.IsCover ? 'Plan sheet cover' : 'Set as cover') + '</button>')
				: '';
			tmpHTML += '<div class="bp-tb-side">' + tmpCoverBtn + '<button class="bp-act bp-act-ghost" onclick="' + tmpSelf + '.back()">Done</button></div></div>';

		tmpPlates.forEach((pPT) =>
		{
			let tmpItems = (tmpByType[pPT.Type] && tmpByType[pPT.Type].Items) || [];
			// A read-only gathered plate with nothing in it is not worth a section; a curatable one still
			// renders empty so "+ Add" stays reachable.
			if (!pPT.Curatable && !tmpItems.length) { return; }
			tmpHTML += '<section class="bp-plate"><header class="bp-plate-head">' + this._icon(pPT.Type) + '<span>' + this._esc(pPT.Label) + '</span>'
				+ '<span class="bp-count">' + tmpItems.length + '</span>'
				+ (tmpCanEdit && pPT.Curatable ? ('<button class="bpc-add" onclick="' + tmpSelf + '.openPicker(\'' + pPT.Type + '\')">+ Add</button>') : '')
				+ '</header><div class="bp-items">';
			if (!tmpItems.length) { tmpHTML += '<div class="bpc-empty">Nothing added yet.</div>'; }
			tmpItems.forEach((pIt) =>
			{
				let tmpLink = pIt.Link ? ('#' + pIt.Link) : '';
				let tmpTitle = tmpLink ? ('<a class="bp-card-link" href="' + this._esc(tmpLink) + '">' + this._esc(pIt.Title) + '</a>') : this._esc(pIt.Title);
				// Pinned (carries an IDBlueprintItem) is something this composer put there and can take back off.
				// Anything else arrived through the auto-gather -- there is no unpin for that, so it gets a tag
				// explaining why instead of a remove control.
				let tmpAutoTag = pIt.Pinned ? '' : '<span class="bpc-auto" title="Included automatically; not removable here">AUTO</span>';
				tmpHTML += '<div class="bp-card bpc-card"><span class="bp-card-ic">' + this._icon(pPT.Type) + '</span>'
					+ '<span class="bp-card-body"><span class="bp-card-title">' + tmpTitle + tmpAutoTag + '</span>'
					+ (pIt.Summary ? ('<span class="bp-card-sum">' + this._esc(pIt.Summary) + '</span>') : '') + '</span>'
					+ (tmpCanEdit && pIt.Pinned ? ('<button class="bpc-x" title="Remove from this blueprint" onclick="' + tmpSelf + '.unpin(' + (pIt.IDBlueprintItem || 0) + ')">&times;</button>') : '')
					+ '</div>';
			});
			tmpHTML += '</div>';

			if (pPT.Curatable && this._picker && this._picker.Type === pPT.Type)
			{
				tmpHTML += '<div class="bpc-picker"><div class="bpc-picker-head"><span>Add a ' + this._esc(pPT.One) + '</span>'
					+ '<button class="bpc-picker-close" onclick="' + tmpSelf + '.closePicker()">close</button></div>';
				if (!this._picker.Candidates.length) { tmpHTML += '<div class="bpc-empty">Nothing left to add.</div>'; }
				else
				{
					tmpHTML += '<div class="bpc-cands">';
					this._picker.Candidates.forEach((pC) =>
					{
						tmpHTML += '<button class="bpc-cand" onclick="' + tmpSelf + '.pin(\'' + pPT.Type + '\',' + (pC.IDItem || 0) + ')">' + this._esc(pC.Title || ('#' + pC.IDItem)) + '</button>';
					});
					tmpHTML += '</div>';
				}
				tmpHTML += '</div>';
			}
			tmpHTML += '</section>';
		});
		tmpHTML += '</div>';
		return tmpHTML;
	}
}

module.exports = PictViewBlueprintComposer;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-BlueprintComposer',
	DefaultRenderable: 'BlueprintComposer-Page',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSSPriority: 542,
	CSS: libBlueprintRender.SHEET_CSS + /*css*/`
		.bp-root .bpc-add { margin-left: 10px; font: inherit; font-size: 12px; padding: 2px 11px; border-radius: 6px; border: 1px solid var(--bp-acc); background: transparent; color: var(--bp-acc); cursor: pointer; letter-spacing: 0; text-transform: none; }
		.bp-root .bpc-add:hover { background: rgba(240,196,74,0.14); }
		.bp-root .bpc-empty { color: var(--bp-eye); font-size: 13px; padding: 7px 2px; }
		.bp-root .bpc-card { position: relative; padding-right: 30px; }
		.bp-root .bpc-x { position: absolute; top: 6px; right: 7px; border: none; background: none; color: var(--bp-eye); font-size: 19px; line-height: 1; cursor: pointer; padding: 0 4px; }
		.bp-root .bpc-x:hover { color: var(--bp-acc); }
		.bp-root .bpc-picker { margin-top: 11px; border: 1px dashed var(--bp-frame); border-radius: 8px; padding: 11px 13px; background: rgba(8,16,30,0.3); }
		.bp-root .bpc-picker-head { display: flex; align-items: center; justify-content: space-between; font-family: ui-monospace, Menlo, monospace; font-size: 11px; color: var(--bp-eye); text-transform: uppercase; letter-spacing: 1.2px; margin-bottom: 9px; }
		.bp-root .bpc-picker-close { border: none; background: none; color: var(--bp-eye); cursor: pointer; font-size: 11px; text-decoration: underline; }
		.bp-root .bpc-cands { display: flex; flex-wrap: wrap; gap: 8px; }
		.bp-root .bpc-cand { font: inherit; font-size: 13px; padding: 6px 12px; border-radius: 7px; border: 1px solid var(--bp-cell); background: rgba(8,16,30,0.42); color: var(--bp-ink); cursor: pointer; text-align: left; max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.bp-root .bpc-cand:hover { border-color: var(--bp-acc); }
		.bp-root .bpc-auto { font-family: ui-monospace, Menlo, monospace; font-size: 9px; letter-spacing: 1px; color: var(--bp-eye); border: 1px solid var(--bp-frame); border-radius: 4px; padding: 1px 4px; margin-left: 7px; vertical-align: middle; }
	`,
	Templates:
	[
		{ Hash: 'BlueprintComposer-Page', Template: /*html*/`<div class="bp-root"><div id="BlueprintComposer-Body"></div></div>` }
	],
	Renderables:
	[
		{ RenderableHash: 'BlueprintComposer-Page', TemplateHash: 'BlueprintComposer-Page', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};
